<template>
  <q-collapsible
    :data-test="CypressTarget('CreditCard')"
    :label="$t('field.payment.creditCard.method')"
    :sublabel="creditCardSubLabel"
    :opened="opened"
    group="paymentMethods"
    icon="far fa-credit-card"
    collapse-icon="expand_more"
    class="q-pb-md"
    @show="$emit('show')"
  >
    <div
      :data-test="CypressTarget('CreditCardForm')"
      class="row justify-start gutter-sm"
    >
      <div class="col-xs-12 col-md-6 col-lg-6 col-xl-5">
        <form-payment-method-credit-card
          id="CardForm"
          :installments="paymentInstallmentsFixed"
          :card-brand-select="cardBrandSelect"
          :color="SET_COLOR.primary"
          @input="updateCreditCard"
        />
        <div class="col-12">
          <q-field
            :data-test="CypressTarget('BillingAddressField')"
          >
            <q-checkbox
              v-model="sameBilling"
              :data-test="CypressTarget('Checkbox')"
              :color="SET_COLOR.primary"
              :label="$t('label.differentBillingInformation')"
            />
          </q-field>
        </div>
        <div
          v-if="!sameBilling"
          class="col-xs-12"
        >
          <component
            :is="billingFormComponent"
            ref="billingForm"
            v-bind="{color: SET_COLOR.primary}"
            @input="updateBilling"
          />
        </div>
      </div>
      <div class="col-xs-12 col-md-6 col-lg-6 col-xl-5 q-mb-md xs-hide sm-hide">
        <plugin-payments-visual-credit-card
          form-selector="#CardForm"
          name-selector=".CardForm-Name input"
          number-selector=".CardForm-Number input"
          cvc-selector=".CardForm-CVC input"
          expiry-selector=".CardForm-ValidDate input"
        />
      </div>
    </div>
    <div class="row justify-start">
      <div class="col-xs-12 col-md-6">
        <q-field
          v-if="discountAvailable"
        >
          <text-checkout-discount-coupon
            :discount="discount"
            @couponApplied="$emit('applyDiscount')"
          />
        </q-field>
        <q-field>
          <text-checkout-discount-resume
            :language="SET_LANGUAGE"
            :currency="SET_MONEY"
            :discount-items="discountItems"
            :discount="discount"
          />
        </q-field>
        <q-field
          :data-test="CypressTarget('Buttons')"
        >
          <q-btn
            :color="SET_COLOR_BUTTON.primary"
            :text-color="SET_COLOR_BUTTON.text.primary"
            :loading="loading"
            :disable="loading"
            :data-test="CypressTarget('Pay')"
            class="full-width"
            @click="$emit('payment')"
          >
            {{ $t('button.pay') }} - {{ finalTotal | money(SET_LANGUAGE, SET_MONEY) }}
          </q-btn>
        </q-field>
      </div>
    </div>
    <modal-status-payment-error
      v-if="statusModal.error"
      v-model="statusModal.error"
      :title="$t('modal.checkout.status.error.title')"
      :message="$t('modal.checkout.status.error.msg')"
      :color="SET_COLOR.paymentStatus.danger"
    >
      <q-btn
        slot="buttons"
        v-close-overlay
        :data-test="CypressTarget('Retry')"
        :label="$t('button.tryAnotherPaymentMethod')"
        color="red-10"
        @click="$emit('try-again', $event)"
      />
    </modal-status-payment-error>
    <modal-status-payment-success
      v-if="statusModal.success"
      v-model="statusModal.success"
      :message="successMessage"
      :color="SET_COLOR.paymentStatus.success"
    >
      <q-btn
        v-if="nextPageButton"
        slot="buttons"
        :data-test="CypressTarget('GoNextScreen')"
        :label="nextPage"
        color="green-10"
        @click="$emit('success')"
      />
    </modal-status-payment-success>
  </q-collapsible>
</template>

<script>
  import MoneyFilter from 'mixins/filters/money';
  import formatMoney from 'helpers/formatters/money';

  export default {
    name: 'CreditCartPaymentMethod',
    components: {
      FormPaymentMethodCreditCard: () => import('components/form/payment/methods/creditCard'),
      ModalStatusPaymentSuccess: () => import('components/modal/status/success'),
      ModalStatusPaymentError: () => import('components/modal/status/error'),
      PluginPaymentsVisualCreditCard: () => import('components/plugins/payments/visualCreditCard'),
      TextCheckoutDiscountCoupon: () => import('components/text/checkout/discountCoupon'),
      TextCheckoutDiscountResume: () => import('components/text/checkout/discountResume'),
    },
    mixins: [MoneyFilter],
    props: {
      paymentResponse: {
        type: Object,
        required: true,
      },
      opened: {
        type: Boolean,
        required: false,
        default: false,
      },
      statusModal: {
        type: Object,
        required: true,
      },
      cartFinalTotal: {
        type: Number,
        require: true,
        default: 0,
      },
      paymentInstallments: {
        type: Array,
        required: true,
      },
      cardBrandSelect: {
        type: Boolean,
        required: false,
        default: false,
      },
      discount: {
        type: Number,
        require: false,
        default: 0,
      },
      discountItems: {
        type: Array,
        required: false,
        default: () => ([]),
      },
      nextPageButton: {
        type: Boolean,
        required: false,
        default: true,
      },
      nextPageButtonText: {
        type: String,
        required: false,
        default: '',
      },
      loading: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    data: () => ({
      sameBilling: true,
      creditCard: {
        number: '',
        holderName: '',
        cvc: '',
        validDate: '',
        installments: 1,
        expiry: {
          month: 0,
          year: 0,
        },
      },
      billing: {},
    }),
    computed: {
      maxInstallment: {
        get() {
          return (this.paymentInstallments.length > 0)
            ? this.paymentInstallments[this.paymentInstallments.length - 1].installments
            : 1;
        },
        cache: false,
      },
      billingFormComponent() {
        if (this.SET_LANGUAGE !== 'pt-br') {
          return () => import('components/form/payment/billing/foreigner');
        }
        return () => import('components/form/payment/billing/brazilian');
      },
      discountAvailable() {
        return (this.discountItems.length > 0 && this.discount);
      },
      nextPage() {
        return this.nextPageButtonText ? this.nextPageButtonText : this.$t('button.viewPurchase');
      },
      paymentInstallmentsFixed: {
        get() {
          return (this.paymentInstallments.length > 0)
            ? this.paymentInstallments.map((i) => {
              const amount = parseFloat(i.amount).toFixed(2);
              const formattedMoney = formatMoney(amount, this.SET_LANGUAGE, this.SET_MONEY);
              return {
                amount,
                installments: i.installments,
                label: `${i.installments}x ${formattedMoney}`,
                stamp: formattedMoney,
                value: i.installments,
              };
            })
            : [];
        },
        cache: false,
      },
      finalTotal() {
        const selectedInstallments = this.paymentInstallments.find((i) => i.installments === this.creditCard.installments);
        return (this.creditCard.installments === 1)
          ? this.cartFinalTotal
          : (selectedInstallments.installments * selectedInstallments.amount);
      },
      successMessage() {
        if (this.nextPageButton) {
          return `${this.$t('modal.checkout.status.success.msg')}${this.$t('modal.checkout.status.success.printDownload')}`;
        }
        return null;
      },
    },
    watch: {
      sameBilling: {
        handler(newValue) {
          if (newValue) {
            this.emitEvent();
          }
        },
        immediate: true,
      },
      creditCard: {
        handler() {
          this.emitEvent();
        },
        immediate: true,
        deep: true,
      },
      billing: {
        handler() {
          this.emitEvent();
        },
        immediate: true,
        deep: true,
      },
    },
    methods: {
      updateCreditCard(payload) {
        this.creditCard = payload;
      },
      updateBilling(payload) {
        if (!this.sameBilling) this.billing = payload;
      },
      emitEvent() {
        const eventData = JSON.parse(JSON.stringify({ ...this.creditCard }));
        if (!this.sameBilling) eventData.billing = JSON.parse(JSON.stringify(this.billing));
        this.$emit('form-data', eventData);
      },
      creditCardSublabel() {
        const installments = this.maxInstallment;
        const subLabel = this.$t('field.payment.creditCard.sublabel', { times: installments });
        const subLabelWithoutInstallment = this.$t('field.payment.creditCard.subLabelWithoutInstallment');

        return installments > 1 ? subLabel : subLabelWithoutInstallment;
      },
    },
  };
</script>
